import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { MyWalletGridContainer } from 'reaxl-wallet';
import { useDevice } from '@atc/react-device';

import { DelayedImpressionObserver, sendImpressions } from 'reaxl-analytics';
import { myWalletKbbTradeInIndicator, walletGridImpression } from 'reaxl-analytics-handlers';

import { eventBus } from '@atc/cai-event-bus';
import {
    WALLET_UPDATED,
    OPEN_WALLET,
    CLOSE_WALLET,
    WALLET_UI_UPDATED,
} from '@syndicated-wallet/constants/walletEvents';
import { paymentsDuck } from '@syndicated-wallet/ducks';
import { useSetWallet, useWalletInitialization, useWalletManager, useWalletId } from '@syndicated-wallet/hooks';

export default function WalletGrid(props = {}) {
    const dispatch = useDispatch();

    const paymentsInfo = useSelector(paymentsDuck.selectors.getPaymentsInfo);

    // a ref to keep track of current paymentsInfo so we can avoid multiple updates
    const currentPaymentsInfo = useRef(paymentsInfo);

    const isPaymentsInfoLoaded = useSelector(paymentsDuck.selectors.getIsPaymentsInfoLoaded);
    const prevIsPaymentsInfoLoaded = useRef(isPaymentsInfoLoaded);

    const { mediaType } = useDevice();

    const gridLayout =
        props[`layout-${mediaType}`] ||
        props['layout-default'] ||
        (mediaType === 'xs' ? 'vertical-grid' : 'horizontal-grid');

    const updatePaymentsInfo = (data) => {
        currentPaymentsInfo.current = { ...data };
        dispatch({
            type: paymentsDuck.types.SET_PAYMENT_INFO,
            payload: { ...data },
        });
    };

    const handleGridReady = (data) => {
        updatePaymentsInfo(data);
        if (data?.tradeIn?.hasNewTradeIn) {
            sendImpressions({ name: myWalletKbbTradeInIndicator });
        }
    };

    const handleClose = () => {
        // update paymentsDuck when modal closes
        dispatch({
            type: paymentsDuck.types.UPDATE_MY_WALLET,
            payload: {
                displayModal: false,
                selectedPanelName: null,
            },
        });
    };

    useEffect(() => {
        if (!prevIsPaymentsInfoLoaded?.current && isPaymentsInfoLoaded) {
            sendImpressions({ name: walletGridImpression });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPaymentsInfoLoaded]);

    useWalletInitialization({
        paymentsDuck,
        onComplete: handleGridReady,
    });

    useEffect(() => {
        eventBus.on(WALLET_UI_UPDATED, updatePaymentsInfo);
        eventBus.on(CLOSE_WALLET, handleClose);

        return () => {
            eventBus.off(WALLET_UI_UPDATED, updatePaymentsInfo);
            eventBus.off(WALLET_UPDATED, updatePaymentsInfo);
            eventBus.off(CLOSE_WALLET, handleClose);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useWalletManager({
        currentPaymentsInfoRef: currentPaymentsInfo,
        paymentsDuck,
    });

    useSetWallet({
        prevPaymentsInfo: currentPaymentsInfo,
        paymentsInfo,
        isPaymentsInfoLoaded,
    });

    useWalletId({
        paymentsDuck,
    });

    const handleUpdateMyWallet = (data) => {
        if (data?.displayModal) {
            eventBus.emit(OPEN_WALLET, data);
        }
    };

    return (
        <DelayedImpressionObserver
            onChange={() => sendImpressions({ name: walletGridImpression, data: { isInview: true } })}
            className="margin-horizontal-2"
        >
            <MyWalletGridContainer
                paymentsDuck={paymentsDuck}
                data-cmp="myWalletGrid"
                isRow={gridLayout === 'horizontal-grid'}
                onUpdateMyWallet={handleUpdateMyWallet}
                sectionComponentName="Section"
                showIcon={mediaType === 'lg'}
                uiContext={props?.ui || 'basic'}
                sectionProps={{
                    // adding a matching id from find-car for style
                    id: 'nativeDealContainer',
                }}
            />
        </DelayedImpressionObserver>
    );
}
